import { useContext, useState, useEffect } from "react";
import Content from "./Content";
import Header from "./Header";
import Modal from "@mui/material/Modal";
import { makeStyles } from "@mui/styles";
import useClientDevice from "hooks/useClientDevice";
import { MasterDataContext } from "contexts/MasterDataContext";
import { useTranslation } from "react-i18next";
import {
  IContentDetails,
  IEneCententEntity,
  IEneContent,
  useFetchLessonContentsQuery,
} from "services/common";
import useToast from "hooks/useToast";
import UnityPlayer from "components/Unity";

const useStyles = makeStyles({
  root: {
    userSelect: "none",
    display: "flex",
    justifyContent: "center",
    width: "100%",
    maxWidth: "90vw",
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translateY(-50%) translateX(-50%)",
    margin: "auto",
  },
  modal: {
    width: "auto",
    display: "inline-flex",
    flexDirection: "column",
  },
});
interface IPlayableContent {
  open: boolean;
  lessonContentList: IContentDetails[] | IEneContent[];
  selectedContentName?: string;
}
export interface IContentPlayerProps {
  contentId: string;
  contentTitle: string;
  playEne?: boolean;
  contentFinished?(value: boolean): void;
  grBook?: boolean;
}
function ContentPlayer({
  contentId,
  contentTitle,
  playEne = false,
  contentFinished,
  grBook = false,
}: IContentPlayerProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const classes = useStyles();
  const [show, setShow] = useState(true);
  const [selectedId, setSelectedId] = useState<string>();
  const { data: contentData } = useFetchLessonContentsQuery(
    { playEne, grBook, contentId },
    {
      skip: !contentId,
    }
  );
  const [content, setContent] = useState<IPlayableContent>({
    open: false,
    lessonContentList: [],
    selectedContentName: contentTitle,
  });
  const { logEvent } = useContext(MasterDataContext);
  const { isTablet } = useClientDevice();

  useEffect(() => {
    if (contentTitle?.length && contentId)
      try {
        if (contentData?._statusCode === 200) {
          const contentList = playEne
            ? (contentData._entity as IEneCententEntity)
                ?._eneContentWithLevelCollection
            : contentData._entity;
          if (Array.isArray(contentList)) {
            setContent({
              open: true,
              lessonContentList: structuredClone(contentList).reverse(),
              selectedContentName: contentTitle,
            });
            const eventName = playEne
              ? "EnEContentPlay"
              : grBook
              ? "GuidedReadingContentPlay"
              : "ContentPlay";
            logEvent(eventName);
          }
        } else throw contentData;
      } catch (error) {
        setContent({
          open: false,
          lessonContentList: [],
          selectedContentName: undefined,
        });
        console.log(error);
      }
  }, [contentData, contentTitle, contentId]);

  const closeHandler = () => {
    setContent({
      open: false,
      lessonContentList: [],
      selectedContentName: undefined,
    });
    contentFinished?.(true);
  };

  const selectedIdHandler = (value?: string) => {
    if (value) {
      setSelectedId(value);
      setShow(true);
    } else {
      setSelectedId(undefined);
      setShow(false);
    }
  };

  return isTablet ? (
    notCompatibleWithTablets()
  ) : (
    <>
      <Modal open={content.open} onClose={closeHandler}>
        <div className={classes.root} style={{ direction: i18n.dir() }}>
          <div className={classes.modal}>
            <Header
              title={content.selectedContentName!}
              onClose={closeHandler}
            />
            <Content
              lessonData={content.lessonContentList}
              playEne={playEne}
              setSelectedId={selectedIdHandler}
            />
          </div>
        </div>
      </Modal>
      {selectedId ? (
        <UnityPlayer
          show={show}
          id={selectedId}
          onClose={() => {
            setShow(false);
            setSelectedId(undefined);
          }}
        />
      ) : null}
    </>
  );

  function notCompatibleWithTablets() {
    if (content.open) {
      toast({
        label: t("Info"),
        message: t("Only Desktop supported"),
        rtl: i18n.dir() === "rtl",
        type: "info",
        toastId: "ContentPlayNotSupoorted",
      });
      setContent({ ...content, open: false });
    }
    return null;
  }
}

export default ContentPlayer;
